import { Box, Button, Card, CardContent, CardHeader, Grid, Link, MenuItem, TextField, Typography, useTheme } from "@mui/material";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { CheckCircleOutlined, ExpandMore } from "@mui/icons-material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import UserProfileAvatar from "./UserProfileAvatar";
import UserProfileTitle from "./UserProfileTitle";
import FormInput from "../forms/form-input/FormInput";
import { phoneNumberWithoutCountryCodeRegex } from "../../common/regex/RegEx";
import UserDataService from "../../services/user-data-service/UserDataService";
import AccountProfileV2Utils, { AccountProfileV2FormType, jobFunctions } from "./AccountProfileV2Utils";
import { useFormContext } from "react-hook-form";
import { useTimezones } from "../../queries/time-zones/UseTimezones";
import FormSelect from "../forms/form-select/FormSelect";
import { MfaSourceTypeEnum } from "../../common/enums/MfaSourceTypeEnum";
import { RestError } from "../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { AuthenticationResultEnum } from "../../common/enums/AuthenticationResultEnum";
import { QRCodeSVG } from "qrcode.react";
//import icon from "../../../assets/images/logo_square.png";
import icon from "../../assets/images/logo_square.png";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import ChangePasswordForm from "../change-password-form/ChangePasswordForm";


export type MfaSourceOption = {
    id: number,
    name: string
}

export type MfaInfo = {
    verified: boolean,
    secret: string,
    authenticationGuid?: string,
    lastOtp: string,
    otpLastVerified: string
}

export default function UserProfileCard() {

    const [changePassword, setChangePassword] = useState(false);
    const isSSO = UserDataService.getIsSSO();
    const { t } = useTranslation();
    const [userState] = useUserContext();
    const [,setAppNotificationState] = useAppNotificationContext();
    const theme = useTheme();
    const [offlineState ] = useOfflineDetectContext();

    // If MFA is required, then don't even present None as an option.
    const mfaSources: MfaSourceOption[] = [
        ...(userState.userSettings?.mfaRequired ? [] : [{ id: 0, name: "None"}]),
        { id: 1, name: "Email"},
        { id: 2, name: "Authenticator App"}
    ];

    // Temporary state for validation of MFA. Could/should this be moved to the AccountSettingsContext?
    // I'm thinking no since this state does not need
    const [mfaInfo, setMfaInfo] = useState<MfaInfo>({
        verified: userState.userSettings?.mfaSource === MfaSourceTypeEnum.Authenticator,
        secret: userState.userSettings?.mfaSecret,
        authenticationGuid: userState.userSettings?.authenticationGuid,
        lastOtp: "",
        otpLastVerified: userState.userSettings?.otpLastVerified ?? ""
    } as MfaInfo);
    
    const [loadingMfaInfo, setLoadingMfaInfo] = useState(false);
    const [mfaDetailsExpanded, setMfaDetailsExpanded] = useState(false);

    const formatQrData = useCallback((secret: string): string => {
        return `otpauth://totp/${encodeURI(userState.userSettings?.mfaAccount ?? "")}?secret=${secret ?? ""}&issuer=eLynx%20Technologies`
    }, [userState.userSettings?.mfaAccount]);

    // If we are configured to use Authenticator, then initialize our qrData with the correct info to
    // recreate the same QR Code that was created initially, otherwise we can leave the QR data blank
    // and only fill it in if and when a user chooses to use Authenticator.
    const [qrData, setQrData] = useState(
        userState.userSettings?.mfaSource === MfaSourceTypeEnum.Authenticator ?
        formatQrData(userState.userSettings.mfaSecret) :
        ""
    );

    const {
            watch,
            setValue,
        } = useFormContext<AccountProfileV2FormType>();    
    
        const onChangePassword = () => {
            setChangePassword(true);
        };
        const onCloseChangePassword = () => {
            setChangePassword(false);
        };
    
        const {data: timezones} = useTimezones();
    
        const [
            selectedSource,
            email, 
            authenticationGuid,
            //watchRecipients
         ] = watch([
            "mfaSource",
            'email',
            "authenticationGuid",
            //'recipients'
        ]);

    const onNewSecret = useCallback(async () => {
        if (!loadingMfaInfo) {
            console.log("Requesting new secret...");
            try {
                setLoadingMfaInfo(true);
                const data = await UserDataService.requestAuthenticatorSecret();
                setMfaInfo({
                    ...mfaInfo,
                    authenticationGuid: data.authenticationGuid,
                    secret: data.secret,
                    verified: false,
                    lastOtp: "",
                    otpLastVerified: ""
                } as MfaInfo);
                setValue("otp", "", { shouldValidate: true });
                setQrData(formatQrData(data.secret));
            }
            catch (error) {

                const restError = error as RestError;

                if (restError.response?.status === 400) {
                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        show: true,
                        message: restError.response?.data?.message,
                        hideContactInfo: true
                    });
                } else {
                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        show: true,
                        error: restError
                    });
                }
            }
            finally{
                setLoadingMfaInfo(false);
            }
        }
    }, [mfaInfo,
        setValue,
        setQrData,
        formatQrData,
        setAppNotificationState,
        loadingMfaInfo
    ]);

    const onMfaSourceChanged = (source: MfaSourceTypeEnum) => {
        //console.log("onMfaSourceChanged: ", source);
        if (source === MfaSourceTypeEnum.Authenticator && !mfaInfo.verified) {
            // if we already have an Authenticator secret (because MFA Authenitcator access has
            // been configured on another customer), then don't request a new secret. Simply
            // update our state so that we display the same QR code. We need to check the
            // userState.userSettings here as that would have been established during the
            // login process. The mfaInfo.secret, however, may contain a secret that we
            // generated here by choosing Authenticator as a source.
            if (userState.userSettings?.mfaSecret) {
                // It's possible that the user could have hit Request New Secret (which would update mfaInfo.authenticationGuid),
                // switched to Email, then back to Authenticator. In that case, we want to keep the new secret info, which
                // if we get here means that it hasn't been verified yet. To catch this case, we can compare the
                // mfaInfo.authenticationGuid to the userState.userSettings.authenticationGuid, which will have the
                // authentication guid from before any changes were made.
                console.log("Initializing QR with pre-existing auth data. Setting verified to", userState.userSettings?.authenticationGuid === mfaInfo?.authenticationGuid);

                setMfaInfo({
                    ...mfaInfo,
                    verified: userState.userSettings?.authenticationGuid === mfaInfo?.authenticationGuid
                });

                setQrData(formatQrData(userState.userSettings?.mfaSecret));

            } else if (!mfaInfo.secret) {
                onNewSecret();
            }
        } else if (mfaInfo.otpLastVerified) {
            setMfaInfo({
                ...mfaInfo,
                otpLastVerified: ""
            });
        }

        // When the auth source changes, always reset the OTP and rerun the validation to ensure
        // that we keep everything in sync properly and don't have the OTP field hidden, but
        // signaling the form as being invalid and preventing the ability to save.
        // NOTE: The delay here is a temporary workaround for the case when switching back to
        // ensure the validation doesn't occur before the underlying value has updated.
        // According to the RHF docs, you're not supposed to put a value from the watch into a
        // useEffect, so I'm not exactly sure what the correct way of doing this is yet.
        setTimeout(() => {
            setValue("otp", "", {shouldValidate: true });
        }, 100);
    }

    const validateOtp = async (otp: string): Promise<boolean | string> => {
        //if (user?.mfaSource !== MfaSourceTypeEnum.Authenticator || mfaInfo?.verified) {
        if (selectedSource !== MfaSourceTypeEnum.Authenticator || mfaInfo?.verified) {
            return true;
        }

        if (otp === undefined || otp === null || otp.length === 0) {
            return t('profile.otpVerificationRequired') as string;
        }

        if (mfaInfo?.lastOtp === otp)
        {
            return false;
        }

        if (isNaN(Number(otp))) {
            return t('profile.otpNotNumeric') as string;
        }

        if (otp.length < 6) {
            return t('profile.otpTooShort') as string;
        }

        const result = await UserDataService.validateOtp(otp, mfaInfo?.authenticationGuid);
        const success = result === AuthenticationResultEnum.Success;

        setMfaInfo({
            ...mfaInfo,
            verified: success,
            lastOtp: otp
        } as MfaInfo);

        return success ? success : t('profile.otpInvalid') as string;
    }

    const toggleExpanded = () => {
        setMfaDetailsExpanded(!mfaDetailsExpanded);
    }

    useEffect(() => {
        if (mfaInfo.authenticationGuid && authenticationGuid !== mfaInfo.authenticationGuid) {
            setValue('authenticationGuid', mfaInfo.authenticationGuid)
        }
    }, [
        authenticationGuid,
        mfaInfo.authenticationGuid,
        setValue,
    ])

    return (
        <>
            <Card
                raised={true}
                sx={{
                    flex: "0 0 auto",
                    margin: theme.spacing(1),
                    padding: theme.spacing(2)
                }}
            >
                <CardHeader
                    sx={{
                        padding: 1
                    }}
                    title={UserProfileTitle()}
                    avatar={UserProfileAvatar() }
                    slotProps={{
                        avatar: {flex: '1 1 auto'},
                        title: { 
                            sx: {
                                fontWeight: "bold",
                                marginLeft: 2 
                            },
                            variant: 'h5',
                        }
                    }}
                />
                
                <CardContent
                    sx={{
                        padding: 1,
                        paddingBottom: theme => `${theme.spacing(1)} !important`
                    }}
                >

                    <Grid container spacing={2}>

                        <Grid size={{ xs: 12 }}>

                            <FormInput
                                name="userName"
                                label={t('profile.name')}
                                helperText=" "
                                readOnly={true}
                                //backgroundColor='transparent'
                                sx={{width: '100%'}}
                            />


                        </Grid>
                        
                        <Grid size={{ xs: 12, sm: 6 }}>

                            <FormInput
                                    id="email"
                                    testId="email"
                                    name="email"
                                    ariaLabel="login and contact"
                                    label={t('profile.emailLabel')}
                                    rules={{
                                        pattern: {
                                            value: /^[A-Za-z0-9+_-][A-Za-z0-9.+_-]{0,63}@[A-Za-z0-9.-]{1,61}\.[A-Za-z]{2,}$/,
                                            message: t('profile.invalidEmail')
                                        },
                                        required: true
                                    }}
                                    helperText=" "
                                    readOnly={offlineState.offline}
                                    sx={{width: '100%'}}
                                />


                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>

                            <FormInput
                                    id="phone"
                                    testId="phone"
                                    ariaLabel="phone number"
                                    name="phone"
                                    label={t('profile.phone')}
                                    rules={{
                                        pattern: {
                                            // Bug 52761: DOC - Add User Error
                                            value: phoneNumberWithoutCountryCodeRegex,
                                            //value: /^(1-)?\d{3}-\d{3}-\d{4}$/,
                                            //value: /^\(\d{3}\) ?\d{3}( |-)?\d{4}|^\d{3}( |-)?\d{3}( |-)?\d{4}/,
                                            message: t('profile.invalidPhone')
                                        }
                                    }}
                                    helperText=" "
                                    readOnly={offlineState.offline}
                                    sx={{width: '100%'}}
                                >
                                    {/* <InputMask mask="999-999-9999" maskPlaceholder=" " alwaysShowMask={true} /> */}
                                </FormInput>


                        </Grid>

     {
                        // !isSSO && !(user?.loginName === 'demo@elynxtech.com') &&
                        !isSSO && !(email === 'demo@elynxtech.com') &&
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Link style={{ cursor: 'pointer' }}
                                aria-label="change password"
                                onClick={() => onChangePassword()}>
                                {t("profile.changePassword")}
                            </Link>
                        </Grid>                        

                    }                    

                        <Grid size={{ xs: 12, sm: 6 }}>

                            <FormInput
                                id="company"
                                testId="company"
                                ariaLabel="company name"
                                name="company"
                                label={t('profile.company')}
                                readOnly={offlineState.offline}
                                sx={{width: '100%'}}
                            />


                        </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>

                            <FormSelect
                                id="timezone"
                                testId="timezones"
                                ariaLabel="timezone"
                                name="timezone"
                                label={t('profile.timeZone')}
                                readOnly={offlineState.offline}
                                sx={{width: '100%'}}
                            >
                                {timezones?.map((o, index) =>
                                    <MenuItem key={`tz-${index}`} value={o.id}>
                                        {o.name}
                                    </MenuItem>)}
                            </FormSelect>

                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormInput
                                id="jobtitle"
                                testId="jobtitle"
                                ariaLabel="job title"
                                name="jobTitle"
                                label={t('profile.jobTitle')}
                                readOnly={offlineState.offline}
                                sx={{width: '100%'}}
                            />


                        </Grid>

                        

                        <Grid size={{ xs: 12, sm: 6 }}>

                            <FormSelect
                                id="jobfunction"
                                testId="jobfunction"
                                ariaLabel="job function"
                                name="jobFunction"
                                label={t('profile.jobFunction')}
                                readOnly={offlineState.offline}
                                sx={{width: '100%'}}
                            >
                                {jobFunctions.map((value, index) =>
                                    <MenuItem key={`jf-${index}`} value={value}>
                                        {value}
                                    </MenuItem>)}
                            </FormSelect>
                        </Grid>

                { !isSSO && userState?.userSettings?.mfaEnabled && (

                    <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormSelect
                                id="mfaSource"
                                testId="mfaSource"
                                ariaLabel="mfaSource"
                                name="mfaSource"
                                label={t('profile.authenticationSource')}
                                readOnly={offlineState.offline}
                                onChange={(e) => onMfaSourceChanged(Number(e.target.value) as MfaSourceTypeEnum)}
                                sx={{width: '100%'}}
                            >
                                {mfaSources.map(value =>
                                    <MenuItem key={`mfa-${value.id}`} value={value.id}>
                                        {value.name}
                                    </MenuItem>)}
                            </FormSelect>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}
                            sx={{
                                display: selectedSource === MfaSourceTypeEnum.Authenticator && mfaInfo.secret ? 'flex' : 'none',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                       
                            <Typography variant='subtitle1' color='textSecondary' gutterBottom sx={{
                                fontWeight: 'bold'
                            }}>
                                <Box>
                                    {t('profile.qrCodeInstruction')}
                                </Box>
                            </Typography>
                            <Box>
                                <QRCodeSVG
                                    id="qrMfa"
                                    size={256}
                                    value={qrData}
                                    bgColor="white"
                                    fgColor='#293955'       // can't use textSecondary here. QRCode element doesn't translate it in properly. Also, theme.palette.text.secondary isn't right either.
                                    imageSettings={{
                                        src: icon,          // eLynx logo in the QR :)
                                        height: 256 * .1,   // Because the QRCode component sometimes throws errors if you don't explicitly set these
                                        width: 256 * .1,    // Because the QRCode component sometimes throws errors if you don't explicitly set these
                                        excavate: true
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    marginTop: theme.spacing(1),
                                    marginBottom: theme.spacing(1)
                                }}>
                                <Button variant="outlined"
                                    onClick={() => onNewSecret()}>
                                    {t('profile.requestNewSecret')}
                                </Button>
                            </Box>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6 }}
                            sx={{
                                display: selectedSource === MfaSourceTypeEnum.Authenticator && mfaInfo.secret ? 'flex' : 'none',
                                flexDirection: 'column',
                                alignItems: 'flex-start'
                            }}>
                            <Typography variant='subtitle2' color='textSecondary'
                                sx={{
                                    width: '100%'
                                }}>
                                <Box
                                    sx={{
                                        display: mfaInfo?.verified ? 'none' : 'flex',
                                        alignItems: 'center'
                                    }}>
                                        {t('profile.authenticatorValidationInfo')}
                                </Box>
                                <FormInput
                                    name="otp"
                                    id="otp"
                                    testId="otp"
                                    label={t("profile.verifyOtp")}
                                    rules={{
                                        validate: (value: string) => selectedSource !== MfaSourceTypeEnum.Authenticator || validateOtp(value)
                                    }}
                                    sx={{
                                        display: mfaInfo?.verified ? 'none' : 'flex',
                                        marginTop: theme.spacing(1),
                                        marginBottom: theme.spacing(1),
                                        maxWidth: '200px'
                                    }}>
                                </FormInput>
                                <Box
                                    sx={{
                                        display: mfaInfo?.verified ? 'flex' : 'none',
                                        alignItems: 'center'
                                    }}>
                                        <CheckCircleOutlined sx={{color: 'green', marginRight: theme.spacing(1)}}></CheckCircleOutlined>{t('profile.authenticatorValidationSuccess')}
                                </Box>
                                <Box
                                    sx={{
                                        display: mfaInfo?.verified && mfaInfo.otpLastVerified ? 'flex' : 'none',
                                        color: 'green',
                                        fontStyle: 'italic'
                                    }}>
                                    {AccountProfileV2Utils.getOtpLastVerifiedText(mfaInfo.otpLastVerified)}
                                </Box>
                                <Box
                                    sx={{
                                        display: mfaInfo?.verified && userState.userSettings?.authenticationGuid !== mfaInfo?.authenticationGuid ? 'flex' : 'none',
                                        marginTop: theme.spacing(1)
                                    }}>
                                    {t('profile.authenticatorNotPersisted')}
                                </Box>
                                <Box
                                    sx={{
                                        marginTop: theme.spacing(1)
                                    }}>
                                    <Button
                                        sx={{
                                            color: "text.primary"
                                        }}

                                        variant="text"
                                        onClick={toggleExpanded}
                                        style={{ marginLeft: "auto" }}
                                        endIcon={<ExpandMore sx={{
                                            transform: !mfaDetailsExpanded ? "rotate(0deg)" : "rotate(180deg)",
                                            transition: theme.transitions.create('transform', {
                                                duration: theme.transitions.duration.shortest,
                                            })
                                        }} />}
                                    >
                                        {t('profile.manualAuthInfo')}
                                    </Button>
                                </Box>
                                <Box
                                    sx={{
                                        display: mfaDetailsExpanded ? 'flex' : 'none',
                                        flexDirection: 'column'
                                    }}>
                                    <TextField
                                        variant="filled"
                                        id="mfaAccount"
                                        name="mfaAccount"
                                        data-testid="mfaAccount"
                                        aria-label="mfaAccount"
                                        label={t('profile.mfaAccount')}
                                        value={userState?.userSettings?.mfaAccount || ""}
                                        sx={{
                                            marginTop: theme.spacing(1),
                                            width: '100%',
                                        }}
                                        slotProps={{
                                            input: {
                                                readOnly: true
                                            },

                                            inputLabel: {
                                                shrink: true
                                            }
                                        }} />
                                    <TextField
                                        variant="filled"
                                        id="mfaAccount"
                                        name="mfaAccount"
                                        data-testid="mfaAccount"
                                        aria-label="mfaAccount"
                                        label={t('profile.mfaSecret')}
                                        value={mfaInfo?.secret || ""}
                                        sx={{
                                            marginTop: theme.spacing(1),
                                            width: '100%',
                                        }}
                                        slotProps={{
                                            input: {
                                                readOnly: true
                                            },

                                            inputLabel: {
                                                shrink: true
                                            }
                                        }} />

                                </Box>
                                <Box
                                    sx={{
                                        marginTop: theme.spacing(2)
                                    }}>
                                </Box>
                            </Typography>
                        </Grid>
                        </>



                )}

                    </Grid>                

               </CardContent>
            </Card>
            <ChangePasswordForm open={changePassword} onClose={onCloseChangePassword} />
        </>
    );
}